import React, { ReactElement } from 'react';

import { SvgIcon } from '@infoods/lifebase-ui';

import classes from './CoachRow.module.scss';
import { ReactComponent as InstagramIcon } from '../../../../shared/assets/icons/instagram.svg';
import { ReactComponent as FacebookIcon } from '../../../../shared/assets/icons/facebook.svg';
import {
  GridTableCell,
  GridTableRow,
  UserInfoCell,
} from '../../../../shared/components/table-components';
import { CoachRowInfo } from '../../../interfaces/coach-list/CoachRowInfo';
import {
  COACH_LIST_COLUMN,
  COACH_LIST_MODE,
  CoachListColumn,
  CoachListMode,
} from '../NgzCoachList.settings';
import { CoachStatusInfo } from '../../../components/CoachStatusInfo/CoachStatusInfo';

type CoachRowProps = {
  coachInfo: CoachRowInfo;
  visibleColumns: CoachListColumn[];
  listMode: CoachListMode;
};

const CoachRow = ({ coachInfo, visibleColumns, listMode }: CoachRowProps) => {
  const linkConfig = {
    target: '_blank',
    rel: 'noopener noreferrer',
  };

  const coachProfilePathConfig: Record<CoachListMode, string> = {
    [COACH_LIST_MODE.NUTRITION]: `#!/nutrition/coach-edit/${coachInfo.id}`,
    [COACH_LIST_MODE.SCAN_IQ]: `#!/scan-iq/coach-edit/${coachInfo.id}`,
  };

  const coachProfilePath = coachProfilePathConfig[listMode];

  const renderColumnsInfo: Record<CoachListColumn, ReactElement> = {
    [COACH_LIST_COLUMN.NAME]: (
      <GridTableCell variant="title">
        <UserInfoCell
          firstName={coachInfo.firstName}
          lastName={coachInfo.lastName}
          email={coachInfo.email}
          phone={coachInfo.cellPhone}
          href={coachProfilePath}
        />
      </GridTableCell>
    ),
    [COACH_LIST_COLUMN.ENTITY]: (
      <GridTableCell className="private-info">{coachInfo.entityName}</GridTableCell>
    ),
    [COACH_LIST_COLUMN.STATE]: (
      <GridTableCell className="private-info">{coachInfo.entityState}</GridTableCell>
    ),
    [COACH_LIST_COLUMN.CITY]: (
      <GridTableCell className="private-info">{coachInfo.entityCity}</GridTableCell>
    ),
    [COACH_LIST_COLUMN.SETUP_COMMISSION]: (
      <GridTableCell className="private-info">
        {coachInfo.commissionSetUp === 0 ? '-' : `${coachInfo.commissionSetUp}%`}
      </GridTableCell>
    ),
    [COACH_LIST_COLUMN.RENEW_COMMISSION]: (
      <GridTableCell className="private-info">
        {coachInfo.commissionRenew === 0 ? '-' : `${coachInfo.commissionRenew}%`}
      </GridTableCell>
    ),
    [COACH_LIST_COLUMN.COACH_DESIGNATION]: (
      <GridTableCell className="private-info">
        {coachInfo.complianceCoach ? 'Compliance' : 'Nutrition'}
      </GridTableCell>
    ),
    [COACH_LIST_COLUMN.STATUS]: (
      <GridTableCell>
        <CoachStatusInfo isActive={coachInfo.active} iconSize="large" />
      </GridTableCell>
    ),
    [COACH_LIST_COLUMN.EXTRA_INFO]: (
      <GridTableCell justify="end" className={classes.socialMedia}>
        {coachInfo.instagram && (
          <a href={coachInfo.instagram} {...linkConfig}>
            <SvgIcon component={InstagramIcon} inheritViewBox fontSize="medium" />
          </a>
        )}
        {coachInfo.facebook && (
          <a href={coachInfo.facebook} {...linkConfig}>
            <SvgIcon component={FacebookIcon} inheritViewBox fontSize="medium" />
          </a>
        )}
      </GridTableCell>
    ),
  };

  return (
    <GridTableRow variant="row" dataTestId="coach-row">
      {visibleColumns.map((col) => (
        <React.Fragment key={col}>{renderColumnsInfo[col]}</React.Fragment>
      ))}
    </GridTableRow>
  );
};

export { CoachRow };
